import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import Card from '@/components/ui/Card'
import { useProspectLockerRoom, PROSPECT_DEFAULT_ANNUAL_CENTS, PROSPECT_DEFAULT_MONTHLY_CENTS } from '@/hooks/useProspectLockerRoom'
import { normalizeImg } from '@/lib/images'

function formatCurrency(cents?: number | null) {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format((cents as number) / 100)
}

function formatPercent(value: number) {
  return `${Math.round(value * 100)}%`
}

function BudgetProgressBar({ spent, total, label }: { spent: number; total: number; label: string }) {
  const percentage = total > 0 ? Math.min(spent / total, 1) : 0
  const isOverBudget = spent > total
  const barColor = isOverBudget ? 'bg-brick' : percentage > 0.8 ? 'bg-amber-500' : 'bg-emerald-500'

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-stone-600">{label}</span>
        <span className={`font-medium ${isOverBudget ? 'text-brick' : 'text-stone-700'}`}>
          {formatCurrency(spent)} / {formatCurrency(total)}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-stone-200">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${Math.min(percentage * 100, 100)}%` }}
        />
      </div>
      <div className="text-right text-xs text-stone-500">{formatPercent(percentage)} used</div>
    </div>
  )
}

function TeammateCard({ prospect }: { prospect: { id: number; name?: string | null; avatar_url?: string | null; status?: string | null } }) {
  const displayName = prospect.name || `Teammate #${prospect.id}`
  const avatarUrl = prospect.avatar_url ? normalizeImg(prospect.avatar_url) : null

  return (
    <div className="flex items-center gap-3 rounded-xl bg-stone-50 p-3">
      {avatarUrl ? (
        <img src={avatarUrl} alt={displayName} className="h-10 w-10 rounded-full object-cover" />
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-stone-300 text-sm font-semibold text-stone-600">
          {(prospect.name?.[0] || '?').toUpperCase()}
        </div>
      )}
      <div>
        <p className="text-sm font-medium text-stone-900">{displayName}</p>
        <p className="text-xs text-stone-500">{prospect.status || 'Active'}</p>
      </div>
    </div>
  )
}

export default function ProspectLockerView() {
  const { loading, error, lockerRoom, coach, budget, credits, teammates, prospectId } = useProspectLockerRoom()

  // Calculate monthly spending (approximation - would need actual monthly data)
  const monthlySpent = useMemo(() => {
    // For now, estimate based on annual spent / months elapsed
    const now = new Date()
    const monthsElapsed = now.getMonth() + 1 // January = 1
    const annualSpent = budget?.spent_cents ?? 0
    return Math.round(annualSpent / monthsElapsed)
  }, [budget?.spent_cents])

  const annualLimit = budget?.annual_limit_cents ?? PROSPECT_DEFAULT_ANNUAL_CENTS
  const monthlyLimit = budget?.soft_monthly_limit_cents ?? PROSPECT_DEFAULT_MONTHLY_CENTS
  const remaining = budget?.remaining_cents ?? (annualLimit - (budget?.spent_cents ?? 0))

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="animate-pulse space-y-4">
          <div className="h-6 w-48 rounded bg-stone-200" />
          <div className="h-4 w-64 rounded bg-stone-200" />
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <Card className="space-y-4 border-amber-200 bg-amber-50">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-100 p-2">
            <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-amber-800">Locker Room Not Found</h3>
            <p className="mt-1 text-sm text-amber-700">{error}</p>
            <p className="mt-2 text-sm text-amber-600">Contact your coach to get added to their locker room.</p>
          </div>
        </div>
      </Card>
    )
  }

  if (!lockerRoom) {
    return (
      <Card className="text-center">
        <p className="text-stone-600">No locker room found.</p>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header - Locker Room Info */}
      <Card className="space-y-4 bg-gradient-to-br from-cocoa to-cocoa/90 text-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-white/70">Your Locker Room</p>
            <h2 className="mt-1 text-xl font-semibold">{lockerRoom.public_name || lockerRoom.name || 'Locker Room'}</h2>
            {coach && (
              <p className="mt-2 text-sm text-white/80">
                Coach: <span className="font-medium text-white">{coach.name || coach.email || 'Your Coach'}</span>
              </p>
            )}
          </div>
          <div className="rounded-xl bg-white/10 px-4 py-2 backdrop-blur">
            <p className="text-xs text-white/70">Locker Credits</p>
            <p className="text-lg font-bold">{credits?.balance_credits ?? 0}</p>
          </div>
        </div>
      </Card>

      {/* Budget Overview */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-stone-500">Your Budget</p>
              <h3 className="text-base font-semibold text-stone-900">Annual Allocation</h3>
            </div>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
              {formatCurrency(remaining)} left
            </span>
          </div>

          <BudgetProgressBar
            spent={budget?.spent_cents ?? 0}
            total={annualLimit}
            label="Annual Budget ($1,200/year)"
          />

          <div className="rounded-xl bg-stone-50 p-3 text-sm">
            <p className="text-xs uppercase tracking-wide text-stone-500">Monthly Limit</p>
            <p className="mt-1 font-semibold text-stone-900">{formatCurrency(monthlyLimit)} per month</p>
            <p className="mt-1 text-xs text-stone-600">
              This is your maximum monthly spending limit set by your coach.
            </p>
          </div>
        </Card>

        <Card className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-stone-500">This Month</p>
              <h3 className="text-base font-semibold text-stone-900">Monthly Spending</h3>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${monthlySpent > monthlyLimit ? 'bg-brick/10 text-brick' : 'bg-stone-100 text-stone-700'}`}>
              {monthlySpent > monthlyLimit ? 'Over limit' : 'On track'}
            </span>
          </div>

          <BudgetProgressBar
            spent={monthlySpent}
            total={monthlyLimit}
            label="Monthly Budget ($100/month)"
          />

          <div className="flex gap-2">
            <Link
              to="/portal/prospect/marketplace"
              className="flex-1 rounded-lg bg-cocoa px-4 py-2 text-center text-sm font-semibold text-white shadow hover:bg-cocoa/90"
            >
              Shop Marketplace
            </Link>
            <Link
              to="/portal/prospect/shopping"
              className="flex-1 rounded-lg border border-stone-300 px-4 py-2 text-center text-sm font-semibold text-stone-700 shadow-sm hover:bg-stone-50"
            >
              View History
            </Link>
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/portal/prospect/tools" className="group">
          <Card className="h-full space-y-2 transition-shadow hover:shadow-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 text-purple-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h4 className="font-semibold text-stone-900 group-hover:text-cocoa">AI Tools</h4>
            <p className="text-xs text-stone-600">Run check-ins and use AI coaching tools</p>
          </Card>
        </Link>

        <Link to="/portal/prospect/updates" className="group">
          <Card className="h-full space-y-2 transition-shadow hover:shadow-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h4 className="font-semibold text-stone-900 group-hover:text-cocoa">Media & Clips</h4>
            <p className="text-xs text-stone-600">Upload photos and videos for highlights</p>
          </Card>
        </Link>

        <Link to="/portal/prospect/goals" className="group">
          <Card className="h-full space-y-2 transition-shadow hover:shadow-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <h4 className="font-semibold text-stone-900 group-hover:text-cocoa">Goals</h4>
            <p className="text-xs text-stone-600">Set and track your fundraising goals</p>
          </Card>
        </Link>

        <Link to="/portal/prospect/profile" className="group">
          <Card className="h-full space-y-2 transition-shadow hover:shadow-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h4 className="font-semibold text-stone-900 group-hover:text-cocoa">My Profile</h4>
            <p className="text-xs text-stone-600">Update your bio and profile info</p>
          </Card>
        </Link>
      </div>

      {/* Teammates */}
      {teammates.length > 0 && (
        <Card className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-stone-500">Your Team</p>
              <h3 className="text-base font-semibold text-stone-900">Teammates in this Locker Room</h3>
            </div>
            <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold text-stone-700">
              {teammates.length + 1} athletes
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {teammates.map((teammate) => (
              <TeammateCard key={teammate.id} prospect={teammate} />
            ))}
          </div>
        </Card>
      )}

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-blue-100 p-2">
            <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-blue-900">About Your Budget</h4>
            <p className="mt-1 text-sm text-blue-800">
              You have a yearly budget of <strong>$1,200</strong> ($100 per month) to shop with our partners
              for training equipment, gear, and other approved items. Your coach manages the overall budget
              pool and can adjust allocations as needed.
            </p>
            <p className="mt-2 text-sm text-blue-700">
              Questions about your budget? Talk to your coach or contact support.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
